import { safeStringify } from "../components/dms_common/DmsUtils";
import { Catalogue, moqData, Scheme, TranformedmoqData } from "../features/createSecondaryOrder/models";
import { ChannelkartNetworkGet } from "./networkService";
import { EnvironmentRegistry } from "./registry/environmentRegistry";
import { sendSlackAlert, SlackChannel } from "./slackService";

async function getSchemes(outletCode: string = "") {
  const response = await ChannelkartNetworkGet(`/getSchemes?loginId=${outletCode}`);
  return response?.data?.features;
}

async function getSchemesUsingPromoService(outletCode: string = "") {
  const url=`${EnvironmentRegistry.getPromoServiceUrl()}/api/v1/promos/fetch?outletcode=${outletCode}`;
  try{
  const response = await ChannelkartNetworkGet(url);
  return response?.data?.data;
  }catch(error){
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from Promo Service API",
      `Failed to fetch data from the API`,
      safeStringify({ error, outletCode ,url})
    );
  }
}

export const getSkuWiseOffers = async (outletCode: string) => {
  try {
    const schemesData = await getSchemes(outletCode);
    const skuWiseOffers: Record<string, Scheme[]> = schemesData?.reduce(
      (acc: Record<string, Scheme[]>, curr: Record<string, any>) => {
        const scheme: Scheme = {
          discountId: curr?.discountId,
          schemeDescription: curr?.schemeDescription,
          startQtyOrVal: curr?.startQtyOrVal,
          endQtyOrVal: curr?.endQtyOrVal,
        };
        acc[curr?.batchCode] = acc[curr?.batchCode] ? [...acc[curr?.batchCode], scheme] : [scheme];
        return acc;
      },
      {}
    );
    return skuWiseOffers ?? {};
  } catch (err) {
    console.log("Error while getting sku-wise offers");
    throw new Error("offers");
  }
}

export const getSkuWiseMOQData = async () => {
  let transformedData: TranformedmoqData = {}
  try{
    const response = await ChannelkartNetworkGet("/v1/orderConstraint");
    const data: moqData[] = response?.data || [];
    if(response.status === 200){
      transformedData = data.reduce((acc, item) => {
        acc[item.sku_code] = {
          minimumOrderQuantity: item.moq ?? 0,
          mov: item.mov ?? 0,
          moqtype: item.type ?? "",
          moqUom: item.uom ?? ""
        };
        return acc;
      }, {} as TranformedmoqData);
    }else{
      throw new Error("Error fetching minimumOrderQuantity Data");
    }
  }catch(err){
    console.log(err)
  }
  finally{
    return transformedData
  }
}

interface ProductCriteria {
  batchCode?: string;
  category?: string;
  subCategory?: string;
  pieceSize?: number;
  brand?: string;
  [key: string]: any; // Index signature to allow string indexing
}

interface SchemeWithCriteria {
  criteria: ProductCriteria;
  schemes: Scheme[];
}

export async function addSchemesUsingPromoService(catalogueList: Catalogue[], outletCode: string = ""): Promise<Catalogue[]> {
  try {
    const promoResponse = await getSchemesUsingPromoService(outletCode);
    // Validate promo response
    if (!Array.isArray(promoResponse)) {
      console.error("Invalid promo response format:", promoResponse);
      return catalogueList;
    }
    // Early return if no promos available
    if (promoResponse.length === 0) {
      return catalogueList;
    }

    // Process all schemes with their criteria
    const schemes: SchemeWithCriteria[] = [];
    
    // Transform and index schemes for matching
    for (const promo of promoResponse) {
      // Skip invalid promos
      if (!promo?.promoId || !Array.isArray(promo.productInfo) || !Array.isArray(promo.slabInfo)) {
        continue;
      }
      
      // Create scheme objects from slab info
      const schemeList: Scheme[] = promo.slabInfo.map((slab: any) => ({
        discountId: promo?.promoId,
        schemeDescription: slab?.description || "",
        startQtyOrVal: slab?.startRange || 0,
        endQtyOrVal: slab?.endRange || 0,
        excludedProducts: promo?.excludedProducts?.batchCodes || [],
        benefit: slab?.benefit || 0,
        promoId: promo?.promoId || "",
        slabUnit: promo?.rangeUnit || "",
        benefitUnit: promo?.promoType === "value" ? "value" : "%",
        qpsFlag: promo?.promoAttribute?.some(
          (item: string) => {
            return (item.toLowerCase() === "qpsaggregationpromo" || item.toLowerCase() === "qpscalculationpromo")
          }
        ),
        priority: promo?.priority,
        promoAttribute: promo?.promoAttribute?.[0] || ""
      }));
      
      // Skip if no valid schemes created
      if (schemeList?.length === 0) {
        continue;
      }
      
      // Add productInfo criteria to each scheme for matching
      for (const productInfo of promo?.productInfo || []) {
        schemes.push({
          criteria: productInfo as ProductCriteria,
          schemes: schemeList
        });
      }
    }
    
    // Match products with schemes based on criteria
    return catalogueList.map(item => {
      // Find all matching schemes for this item
      const matchedSchemes = findMatchingSchemes(item, schemes);
      
      // Filter out schemes where this product is excluded
      const applicableSchemes = matchedSchemes.filter(
        scheme => !scheme.excludedProducts?.includes(item.batchCode)
      );
      
      // Apply schemes if found
      if (applicableSchemes.length > 0) {
        return {
          ...item,
          schemes: applicableSchemes
        };
      }
      
      return { ...item };
    });
  } catch (error) {
    console.log("Error while getting sku-wise offers");
    throw new Error("offers");
  }
}

/**
 * Finds all schemes that match the given catalogue item
 */
function findMatchingSchemes(item: Catalogue, schemes: SchemeWithCriteria[]): Scheme[] {
  const matchedSchemes: Scheme[] = [];
  
  for (const scheme of schemes) {
    const criteria = scheme.criteria;
    
    // Skip if any specified criteria doesn't match
    if (!matchesCriteria(item, criteria)) {
      continue;
    }
    
    // Add all schemes for this matching criteria
    matchedSchemes.push(...scheme.schemes);
  }
  
  return matchedSchemes;
}

/**
 * Type-safe check of a specific property between item and criteria
 */
function checkPropertyMatch<K extends keyof Catalogue>(
  item: Catalogue, 
  criteria: ProductCriteria,
  property: K
): boolean {
  // If criteria doesn't have this property or it's null/undefined, it's a match
  if (criteria[property] === undefined || criteria[property] === null) {
    return true;
  }
  
  // If item doesn't have this property or values don't match, it's not a match
  const itemValue = item[property];
  const criteriaValue = criteria[property];
  
  if (itemValue === undefined || itemValue === null) {
    return false;
  }
 
  if (typeof itemValue === 'string' && typeof criteriaValue === 'string') {
    return itemValue.toLowerCase() === criteriaValue.toLowerCase();
  }

  if (typeof itemValue === 'number') {
    const numCriteria = Number(criteriaValue);
    if (isNaN(numCriteria)) {
      return false;
    }
    return itemValue === numCriteria;
  }

  // Fallback to strict equality for other types
  return itemValue === criteriaValue;
}

/**
 * Checks if an item matches all specified criteria fields in a type-safe way
 */
function matchesCriteria(item: Catalogue, criteria: ProductCriteria): boolean {
  // Type-safe checks for known properties
  if (!checkPropertyMatch(item, criteria, 'batchCode')) return false;
  if (!checkPropertyMatch(item, criteria, 'category')) return false;
  if (!checkPropertyMatch(item, criteria, 'subCategory')) return false;
  if (!checkPropertyMatch(item, criteria, 'brand')) return false;
  if (!checkPropertyMatch(item, criteria, 'pieceSize')) return false;
  if (!checkPropertyMatch(item, criteria, 'mrp')) return false;
  
  // Additional checks for any other properties in criteria
  // This is done in a separate function to handle the type mismatch
  for (const key in criteria) {
    if (
      key !== 'batchCode' && 
      key !== 'category' && 
      key !== 'subCategory' && 
      key !== 'brand' && 
      key !== 'pieceSize' &&
      key !== 'mrp'
    ) {
      // For other properties, we need to check if they exist on the item
      // We need to use type assertion here as we can't know all properties at compile time
      const itemValue = (item as any)[key];
      const criteriaValue = criteria[key];
      
      if (criteriaValue !== undefined && criteriaValue !== null) {
        if (itemValue === undefined || itemValue === null || itemValue !== criteriaValue) {
          return false;
        }
      }
    }
  }
  
  // All specified criteria matched
  return true;
}
